import { SELECTION_ACTIONS } from './consts';
import {
  type GitlabV2SelectionAction,
  type GitlabV2SelectionState,
} from './types';

export const initialSelectionState: GitlabV2SelectionState = {
  selectedGroupIds: new Set(),
  selectedProjectIds: new Set(),
};

const toggle = (set: ReadonlySet<number>, id: number): Set<number> => {
  const next = new Set(set);

  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }

  return next;
};

const removeAll = (set: ReadonlySet<number>, ids: number[]): Set<number> => {
  const next = new Set(set);

  ids.forEach((id) => next.delete(id));

  return next;
};

/**
 * Select-step tree selection reducer. Immutable — every case returns a fresh
 * state with new Sets, so the input state is never mutated.
 * @param state - Current selection state.
 * @param action - Selection action.
 * @returns The next selection state.
 */
export const selectionReducer = (
  state: GitlabV2SelectionState,
  action: GitlabV2SelectionAction,
): GitlabV2SelectionState => {
  switch (action.type) {
    case SELECTION_ACTIONS.TOGGLE_GROUP:
      return {
        ...state,
        selectedGroupIds: toggle(state.selectedGroupIds, action.id),
      };
    case SELECTION_ACTIONS.TOGGLE_PROJECT:
      return {
        ...state,
        selectedProjectIds: toggle(state.selectedProjectIds, action.id),
      };
    case SELECTION_ACTIONS.PRESELECT:
      return {
        ...state,
        selectedGroupIds: new Set([
          ...state.selectedGroupIds,
          ...action.groupIds,
        ]),
        selectedProjectIds: new Set([
          ...state.selectedProjectIds,
          ...action.projectIds,
        ]),
      };
    case SELECTION_ACTIONS.DESELECT:
      return {
        ...state,
        selectedGroupIds: removeAll(state.selectedGroupIds, action.groupIds),
        selectedProjectIds: removeAll(
          state.selectedProjectIds,
          action.projectIds,
        ),
      };
    case SELECTION_ACTIONS.RESET:
      return initialSelectionState;
    default:
      return state;
  }
};
